import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonSet from '@jetbrains/ring-ui/components/button-set/button-set'
import classNames from 'classnames'
import * as React from 'react'

import {useAppSelector} from '../../../hooks/react-redux'
import {getIsAllSubtreeCollapsed} from '../../../selectors'
import {getDefaultOverviewTree, getProjectSubTree} from '../../../selectors/projectTrees'
import type {ProjectId} from '../../../types'
import {cancelPostponedCallback, requestPostponedCallback} from '../../../utils/postponedTask'

import {useProjectUnfolderCommonLogics} from './ProjectToolBar.hooks'
import {
  CollapseButtonEnum,
  EXPAND_ITEMS_PER_MOMENT,
  groupIdsByEntityType,
  MAX_ITEMS_TO_EXPAND,
} from './ProjectToolBar.utils'

import styles from './ProjectToolBar.css'

interface Props {
  projectId: ProjectId
  onlyFavorites: boolean
  className?: string
}

function ProjectToolBar({projectId, onlyFavorites, className}: Props) {
  const subprojectTree = useAppSelector(state =>
    onlyFavorites ? getDefaultOverviewTree(state) : getProjectSubTree(state, projectId),
  )

  const allItemsExpanded = useAppSelector(state => getIsAllSubtreeCollapsed(state, subprojectTree))

  const {raf, processing, setProcessing, resetLoadingState, collapseEntities, expandEntities} =
    useProjectUnfolderCommonLogics()

  const collapseAll = React.useCallback(() => {
    raf.current.forEach(postponedTask => cancelPostponedCallback(postponedTask))
    raf.current = []
    setProcessing(false)

    if (!subprojectTree?.data.length) {
      return
    }

    const {buildTypeIds, projectIds} = groupIdsByEntityType(subprojectTree.data)
    collapseEntities(buildTypeIds, projectIds)
  }, [collapseEntities, raf, setProcessing, subprojectTree?.data])

  const expandAll = React.useCallback(() => {
    if (!subprojectTree?.data.length || allItemsExpanded || processing) {
      return
    }

    raf.current.forEach(postponedTask => cancelPostponedCallback(postponedTask))
    raf.current = []

    const entities = subprojectTree.data

    resetLoadingState()

    setProcessing(true)
    for (let i = 0; i < entities.length; i += EXPAND_ITEMS_PER_MOMENT) {
      raf.current.push(
        requestPostponedCallback(
          () => {
            const entitiesToExpand = entities.slice(i, i + EXPAND_ITEMS_PER_MOMENT)
            const {buildTypeIds, projectIds} = groupIdsByEntityType(entitiesToExpand)
            expandEntities(buildTypeIds, projectIds)
          },
          {timeout: 60, fallbackToRAF: true},
        ),
      )
    }
  }, [
    allItemsExpanded,
    expandEntities,
    processing,
    raf,
    resetLoadingState,
    setProcessing,
    subprojectTree?.data,
  ])

  React.useEffect(() => {
    if (allItemsExpanded) {
      setProcessing(false)
    }
  }, [allItemsExpanded, setProcessing])

  if (!subprojectTree) {
    return null
  }

  return (
    <ButtonSet className={classNames(className, styles.buttonSet)}>
      {subprojectTree.data?.length <= MAX_ITEMS_TO_EXPAND && (
        <Button onClick={expandAll} loader={processing}>
          {CollapseButtonEnum.EXPAND_ALL}
        </Button>
      )}
      <Button onClick={collapseAll}>{CollapseButtonEnum.COLLAPSE_ALL}</Button>
    </ButtonSet>
  )
}

export default React.memo<Props>(ProjectToolBar)
